import React, { useMemo, useRef, useState } from "react";
import _ from "lodash";

import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import Button from "../../themed/button";
import Conditional from "../../common/conditional";
import CustomVisualInspectionSummary from "../../custom-reports/custom-utm/custom-utm-summary";
import CustomPDFExport from "../pdf-export";
import CustomCoverPage from "../../custom-reports/custom-general/custom-cover-page";
import LoadingBadge from "../../themed/loading-badge";
import Modal from "../../themed/modal";
import NoItems from "../no-items";
import Select from "../../themed/select/select";

import anomalyService from "../../../services/anomalyService";
import useProjectDetails from "../../../hooks/useProjectDetails";
import config from "../../../utils/config";

const ReportAnomaly: React.FC = () => {
    const [isExporting, setExporting] = useState(false);
    const [isAnomalyVisible, setAnomalyVisible] = useState(false);
    
    const { data, isFetching, refetch } = useAnomalyRecords();
    const { projectDetails } = useProjectDetails();
    const { selectedClass, onChangeClass } = useAnomalyClass();

    const timer = useRef<NodeJS.Timeout>(null);

    const toggleReportAnomaly = (): void => {
		setAnomalyVisible((previous: boolean) => !previous);
	};

    const anomalyClasses = useMemo(() => {
        const classes = data.data.anomalies.map((anomaly) => anomaly.class);
        return _.uniq(classes).map((anomalyClass) => ({ id: anomalyClass, name: anomalyClass }));
    }, [data.data.anomalies]);

    const anomalies = useMemo(() => {
        if (selectedClass) {
            return data.data.anomalies.filter((anomaly) => anomaly.class === selectedClass);
        }

        return data.data.anomalies;
    }, [selectedClass, data]);

    if (!projectDetails || !projectDetails.report.savedProgressUrl || !config.trackableTemplates.includes(projectDetails.inspectionTypeId)) {
        return null;
    }

    return ( 
        <>
            <Modal style={{ height: data.data.anomalies.length === 0 ? 'auto' : '70%' }} isVisible={isAnomalyVisible} onClose={toggleReportAnomaly}>
                <div className="p-1">
                    <Conditional isVisible={isFetching}>
                        <LoadingBadge message="Fetching anomalies..." />
                    </Conditional>

                    <Conditional isVisible={!isFetching}>
                        <NoItems
                            isVisible={data.data.anomalies.length === 0}
                            onRefresh={refetch}
                        />

                        <Conditional isVisible={data.data.anomalies.length > 0}>
                            <Conditional isVisible={isExporting}>
                                <div className="mt-2">
                                    <LoadingBadge message="Exporting to PDF..." />
                                </div>
                            </Conditional>

                            <CustomPDFExport>
                                {(params) => (
                                    <>
                                        <Conditional isVisible={!isExporting}>
                                            <div className="d-flex justify-content-end align-items-center mt-2">
                                                <div className="d-flex justify-content-center align-items-center bg-light border rounded pt-2 ps-2 pe-2">
                                                    <Select
                                                        options={anomalyClasses}
                                                        label=""
                                                        name="class"
                                                        placeholder="All Anomaly Classes"
                                                        value={selectedClass}
                                                        onChange={onChangeClass}
                                                    />

                                                    <div className="ms-2">
                                                        <Button
                                                            icon="download"
                                                            label="Download"
                                                            style={{ marginBottom: "0.5rem", marginTop: 0 }}
                                                            onClick={() => {
                                                                setExporting(true);

                                                                if (timer.current) {
                                                                    clearTimeout(timer.current);
                                                                }

                                                                const timeout = setTimeout(async () => {
                                                                    await params.onDownload();

                                                                    setExporting(false);
                                                                    clearTimeout(timeout);
                                                                }, 3_000);
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </Conditional>

                                        {projectDetails && <CustomCoverPage reportTitle={projectDetails.documentNumber} />}
                                    
                                        <CustomVisualInspectionSummary
                                            anomalyItems={anomalies}
                                            sectionTitle="Anomaly Report"
                                            sectionSubtitle="Inspected Anomalies"
                                        />
                                    </>
                                )}
                            </CustomPDFExport>
                        </Conditional>
                    </Conditional>
                </div>
            </Modal>

            <Button
                label="View Report Anomalies"
                icon="flag-fill"
                onClick={toggleReportAnomaly}
                style={{ backgroundColor: "#ffff0f", color: "#000000", marginLeft: "1rem" }}
            />
        </>
    );
};

const useAnomalyRecords = () => {
    const { projectId } = useParams<{ projectId: string; }>();
    
    return useQuery({
        queryKey: ['anomaly-report', projectId],
        queryFn: () => anomalyService.getAnomalyRecords(projectId!),
        initialData: {
            message: '',
            data: {
                anomalies: [],
                projectId: projectId!,
                createdAt: '',
            },
        }
    });
};

const useAnomalyClass = () => {
    const [selectedClass, setSelectedClass] = useState<string | number>();

    const handleClassChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedClass(event.target.value);
    };

    return { selectedClass, onChangeClass: handleClassChange };
};
 
export default ReportAnomaly;